import React from 'react';
import { ArrowDown, UserRound, Briefcase, Mic, GraduationCap, Users, BookOpen, Award, Compass, MessagesSquare, Building2 } from 'lucide-react';
import { Reveal } from '../Reveal';
import { Cta } from '../Cta';
import { useScore } from '../ScoreContext';
const readerPath = ['Find what applies', 'Understand where they are', 'Take a useful next move', 'Indicate what they need'];
const knownContext = ['What matters', 'Where they are stuck', 'Actions they have taken', 'What kind of help may fit'];
const businessPaths = [{
  label: 'Coaching',
  Icon: UserRound
}, {
  label: 'Consulting',
  Icon: Briefcase
}, {
  label: 'Speaking',
  Icon: Mic
}, {
  label: 'Training',
  Icon: GraduationCap
}, {
  label: 'Workshops',
  Icon: Users
}, {
  label: 'Courses',
  Icon: BookOpen
}, {
  label: 'Certification',
  Icon: Award
}, {
  label: 'Advisory',
  Icon: Compass
}, {
  label: 'Community',
  Icon: MessagesSquare
}, {
  label: 'Team implementation',
  Icon: Building2
}];
const enablementGroups = [{
  label: 'Get in the room',
  items: ['Podcast and interview preparation', 'Keynote and speaking angles']
}, {
  label: 'Create the next experience',
  items: ['Workshop outlines', 'Training material', 'CTA paths']
}, {
  label: 'Continue the conversation',
  items: ['Follow-up assets', 'Offer language', 'Sales conversations', 'Content angles']
}];
function Down() {
  return <div className="flex justify-center py-3" data-id="element-823" data-mp="2m0i2hsznl78">
      <ArrowDown className="h-6 w-6 text-seaglass-light/70" aria-hidden="true" data-id="element-824" data-mp="2l8lp4rkhjfw" />
    </div>;
}
function Chips({
  items
}: {
  items: string[];
}) {
  return <div className="mt-3 flex flex-wrap gap-2 md:mt-0" data-id="element-825" data-mp="3ot8egulxkhr4">
      {items.map(item => <span key={item} className="rounded-lg border border-white/12 bg-navy-light/40 px-3 py-1.5 text-sm text-paper/85" data-id="element-826" data-mp="3or5jdw5x7yfj">
          {item}
        </span>)}
    </div>;
}
function Stage({
  n,
  title,
  children
}: {
  n: string;
  title: string;
  children: React.ReactNode;
}) {
  return <div className="rounded-2xl border border-white/12 bg-white/[0.05] p-5 md:grid md:grid-cols-[15rem_1fr] md:items-center md:gap-6 md:p-6" data-id="element-827" data-mp="3kr3ms1f1lv74">
      <div className="flex items-center gap-3" data-id="element-828" data-mp="1lkyet3inukxm">
        <span className="text-sm font-semibold text-seaglass-light/70" data-id="element-829" data-mp="1lj56zkwb9uo1">{n}</span>
        <p className="font-serif text-xl text-paper md:text-2xl" data-id="element-830" data-mp="1gmn2mm4w7yc8">{title}</p>
      </div>
      <div className="mt-3 md:mt-0" data-id="element-831" data-mp="1ckt6mr5n7iie">{children}</div>
    </div>;
}
export function RevenueArchitecture() {
  const {
    openDiagnostic
  } = useScore();
  return <section className="bg-navy px-6 pb-24 pt-6 text-paper md:px-10 md:pb-28 md:pt-8" data-id="element-832" data-mp="297wrrr7knaqs">
      <div className="mx-auto max-w-5xl" data-id="element-833" data-mp="24bzdrrjs6gh6">
        <div className="max-w-3xl" data-id="element-834" data-mp="2092ya9pjf0hg">
          <Reveal data-id="element-835" data-mp="2081ar5rc4liu">
            <h2 className="font-serif text-3xl leading-[1.08] text-paper md:text-5xl" data-id="element-836" data-mp="20bp70co5zm19">
              A better reader path creates{' '}
              <span className="italic text-seaglass-light" data-id="element-837" data-mp="ou5tnrgve2oz">
                better business opportunities.
              </span>
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-paper/70" data-id="element-838" data-mp="jtrnyg2qr4u4">
              Readers get help using the book. You gain the context, assets, and
              pathways to connect the right people to the right work.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1} data-id="element-839" data-mp="34j4cu5jfv6ti">
          <div className="mt-12" data-id="element-840" data-mp="34htdqj0xun8q">
            <Stage n="1" title="The book" data-id="element-841" data-mp="39a2w9dzq4l0z">
              <p className="text-sm text-paper/60" data-id="element-842" data-mp="39b4imem6wty0">
                Ideas, frameworks, stories, language, and offers
              </p>
            </Stage>
            <Down data-id="element-843" data-mp="3j8acfum6uz1x" />
            <Stage n="2" title="A better reader path" data-id="element-844" data-mp="3j7r3v4ifhn0k">
              <Chips items={readerPath} data-id="element-845" data-mp="3jadbt598e4jb" />
            </Stage>
            <Down data-id="element-846" data-mp="3o8etblr0dvso" />
            <Stage n="3" title="Known context + intent" data-id="element-847" data-mp="3o7vc6796nbfp">
              <Chips items={knownContext} data-id="element-848" data-mp="3s9gwgdo1qrzx" />
            </Stage>
            <Down data-id="element-849" data-mp="3s6umk7doo0ej" />
            <Stage n="4" title="Fitting business paths" data-id="element-850" data-mp="1ttcau80dndz9">
              <div className="flex flex-wrap gap-2" data-id="element-851" data-mp="1tux3tt71mov0">
                {businessPaths.map(({
                label,
                Icon
              }) => <span key={label} className="inline-flex items-center gap-2 rounded-lg border border-seaglass/30 bg-white/[0.06] px-3 py-1.5 text-sm text-paper" data-id="element-852" data-mp="12kr6rrmxi28m">
                    <Icon className="h-4 w-4 flex-shrink-0 text-seaglass-light" aria-hidden="true" data-id="element-853" data-mp="yiw8kw0ao4z2" />
                    {label}
                  </span>)}
              </div>
            </Stage>
            <Down data-id="element-854" data-mp="17kdk012rbihf" />
            {/* Dominant final outcome */}
            <div className="rounded-3xl border-2 border-sand bg-sand/15 px-6 py-10 text-center md:py-14" data-id="element-855" data-mp="jtq7t3lt66p2">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sand/80" data-id="element-856" data-mp="evya1syhfqcy">
                The destination
              </p>
              <p className="mt-3 font-serif text-3xl font-semibold text-sand md:text-5xl" data-id="element-857" data-mp="tm5taawbddep">
                Better-fit business opportunity
              </p>
            </div>

            {/* Sales-enablement supporting rail, tied to the outcome above */}
            <div className="mt-4 rounded-2xl border border-white/12 border-t-2 border-t-sand/40 bg-white/[0.03] p-6 md:p-7" data-id="element-858" data-mp="3sauutq0yz40x">
              <p className="text-sm font-medium text-paper/80" data-id="element-859" data-mp="27qhe8sbzsikh">
                The same build helps the author create and support those
                opportunities.
              </p>
              <div className="mt-5 grid gap-8 md:grid-cols-3 md:gap-10" data-id="element-860" data-mp="1ysnipjm1xsyd">
                {enablementGroups.map(group => <div key={group.label} data-id="element-861" data-mp="22tqyy780hr04">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-sand md:text-[0.8125rem]" data-id="element-862" data-mp="2qiik4en6p93e">
                      {group.label}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2" data-id="element-863" data-mp="2vgts0jtx59iz">
                      {group.items.map(item => <span key={item} className="rounded-lg border border-sand/25 bg-sand/10 px-3 py-1.5 text-[0.9375rem] leading-snug text-paper/85" data-id="element-864" data-mp="2hhc384gmhw9m">
                          {item}
                        </span>)}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15} data-id="element-865" data-mp="3cqxplmz8ehw4">
          <div className="mx-auto mt-12 max-w-3xl text-center" data-id="element-866" data-mp="3cr77soe1bkr9">
            <p className="font-serif text-2xl leading-snug text-paper md:text-3xl" data-id="element-867" data-mp="37rukk2j94bo5">
              Author Leverage helps the author get more business value from the
              book by making it more useful to readers and more usable across the
              business.
            </p>
            <p className="mt-5 text-sm leading-relaxed text-paper/60" data-id="element-868" data-mp="2yxg43wmziv3g">
              The reader experience is the mechanism. Author leverage is the
              outcome. Revenue architecture and sales enablement make that
              leverage commercially useful.
            </p>
            <div className="mt-9 flex justify-center" data-id="element-869" data-mp="2k2jsd9fyvplm">
              <Cta variant="primary" showArrow onClick={openDiagnostic} className="px-10 py-4 text-lg" data-id="element-870" data-mp="2g16o33sivugt">
                Get Your Reader-to-Revenue Score
              </Cta>
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}